// The VIP win-back FOLLOW-UP: one short note, on the same thread, with a date on it.
//
// Greg, 2026-10-12: "I'd like to do a follow-up to the 2026 lapsed that didn't respond, and give
// them a deadline date." The first email (lib/winbackEmail.js) made the whole case and named no
// date, so there was never a reason to answer this week rather than next.
//
// ⚠️ IT IS SHORT ON PURPOSE. It goes out as a reply on the first email, so everything that email
// said is quoted directly underneath. Repeating the perk list here turns a personal nudge into a
// second mailshot, and a second mailshot to somebody who ignored the first is what gets reported
// as spam.
//
// ⚠️ IT DOES NOT SAY "BELOW". Nearly every one is threaded, but when the first email cannot be
// found in the rep's Sent Items the follow-up goes as its own message with nothing underneath.
// "I wrote to you on September 22" is true either way.
//
// ⚠️ THE LAST LINE IS THE "NO THANKS" AND IT IS NOT A COURTESY. A one-to-one email from a rep
// has no unsubscribe link, so until now the only way to make these stop was the spam button, and
// a report counts against crabplace.com - the domain order confirmations go out on. A reply that
// is plainly "no thanks" takes them off the VIP offers automatically (applyNoThanks in
// netlify/functions/lib/vip-winback-followup.js). It says "ask", not "email": the newsletter is
// a separate thing they have not asked to leave.
//
// ⚠️ EVERY TERM COMES FROM winbackTerms.js. Same offer as the first email, to the letter: free
// through March 31, 2027, then $79.99 on April 1, 2027. The only new fact is the date to reply by.
//
// ⚠️ THE SUBJECT IS ONLY USED WHEN IT CANNOT BE THREADED. A threaded reply keeps the first
// email's subject with "RE:" in front, which is what makes a mailbox file it as one conversation.
// No "free", no "offer", no "last chance", no exclamation mark - see the subject notes in
// winbackEmail.js for what those cost a mailbox the team also answers customers from.
//
// Brand rules apply (customer-facing): no em or en dashes, no AI tells. Checked with `npm run brand`.

import { OFFER_TERMS, FOLLOWUP_TERMS } from './winbackTerms';

const money = (n) => '$' + Number(n).toFixed(2);

const firstNameOf = (full) => {
  const n = String(full || '').trim();
  if (!n || n === '(no name on file)') return 'there';
  return n.split(/\s+/)[0];
};

// "2026-09-22" -> "September 22". No year: it was a few weeks ago and the year reads as a form.
const longDay = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  return Number.isNaN(+d) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

export function buildWinbackFollowupEmail(customer, rep) {
  // ⚠️ THE DATE COMES FROM THE CUSTOMER'S OWN WAVE (2026 and 2025 promise different days). No
  // default: an email with the wrong deadline is worse than no email.
  const wave = FOLLOWUP_TERMS.waves[customer.lapseYear];
  const claimByLabel = customer.claimByLabel || (wave && wave.claimByLabel);
  if (!claimByLabel) throw new Error('No reply-by date for ' + (customer.name || 'this customer') + ' (lapse year ' + customer.lapseYear + ').');
  const first = firstNameOf(customer.name);
  const when = longDay(customer.firstSent);
  const subject = `${first}, following up on your Crab Place VIP Club membership`;

  const html = `
<div style="font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#2d3748;max-width:600px">
  <p style="margin:0 0 16px">Hi ${first}, I wrote to you${when ? ' on ' + when : ' a little while ago'} about
  your VIP Club membership and wanted to make sure it reached you.</p>

  <p style="background:#fff3ed;border-left:3px solid #DC582A;padding:14px 16px;margin:18px 0">
    <b style="color:#00395C;font-size:16px">We can still put your membership back at no charge, and I am
    holding that open until ${claimByLabel}.</b><br>
    Nothing to pay today. You would be covered through ${OFFER_TERMS.freeThroughLabel}. After that it renews
    at ${money(OFFER_TERMS.renewalPrice)} on ${OFFER_TERMS.renewsOnLabel}, and you can turn that off whenever
    you like.
  </p>

  <p style="margin:0 0 16px">Fall is the best crab of the year, and members get 8% off market pricing on
  all seafood in the store.</p>

  <p style="margin:0 0 16px"><b>Just reply "I'm in" and I will set it up for you.</b> There is nothing
  for you to fill in.</p>

  <p style="margin:0 0 22px;color:#59707E">If you would rather pass, reply "no thanks" and I will not ask again.</p>

  <p style="margin:0 0 6px">Thanks ${first},</p>

  <p style="margin:0">
  <b style="color:#00395C">${rep.name || 'The Crab Place'}</b><br>
  <span style="color:#59707E;font-size:14px">The Crab Place${rep.email ? ' &middot; ' + rep.email : ''}</span></p>
</div>`.trim();

  return { subject, html, to: customer.email || '' };
}
